import type { I18n } from '@learnforge/i18n';
import type { DiscontinuityKind, Interval } from '../func';
import { createAxes } from './axes';
import { createSvgEl } from '@learnforge/demo-render';

export interface PiecewiseGraphPiece {
  interval: Interval;
  sample: Float64Array;
  className: string;
  label?: string;
}

export interface PiecewiseGraphOptions {
  xDomain: [number, number];
  yDomain: [number, number];
  width: number;
  height: number;
  pieces: readonly PiecewiseGraphPiece[];
  endpointMarkers: readonly { x: number; y: number; kind: 'open' | 'closed' }[];
  discontinuityTicks: readonly { x: number; kind: DiscontinuityKind }[];
  i18n: I18n<string>;
}

export interface PiecewiseGraphHandle {
  group: SVGGElement;
  update(partial: Partial<PiecewiseGraphOptions>): void;
  destroy(): void;
}

export function createPiecewiseGraph(
  parent: SVGGElement,
  opts: PiecewiseGraphOptions,
): PiecewiseGraphHandle {
  const root = createSvgEl('g', { class: 'fn-piecewise-graph' });
  parent.appendChild(root);

  let state: PiecewiseGraphOptions = { ...opts };

  function render(): void {
    root.replaceChildren();

    const axes = createAxes(root, {
      xDomain: state.xDomain,
      yDomain: state.yDomain,
      width: state.width,
      height: state.height,
      showGrid: true,
    });

    for (const piece of state.pieces) {
      const n = piece.sample.length;
      const [pieceStart, pieceEnd] = [piece.interval.start, piece.interval.end];
      let d = '';
      let move = true;
      let lastFiniteIdx = -1;
      for (let i = 0; i < n; i++) {
        const xVal = pieceStart + ((pieceEnd - pieceStart) * i) / Math.max(1, n - 1);
        const yVal = piece.sample[i] as number;
        if (!Number.isFinite(yVal)) {
          move = true;
          continue;
        }
        const sx = axes.toScreenX(xVal);
        const sy = axes.toScreenY(yVal);
        d += `${move ? 'M' : 'L'} ${sx.toFixed(2)} ${sy.toFixed(2)} `;
        move = false;
        lastFiniteIdx = i;
      }
      root.appendChild(createSvgEl('path', { d: d.trim(), class: piece.className, fill: 'none' }));

      if (piece.label && lastFiniteIdx >= 0) {
        const xVal = pieceStart + ((pieceEnd - pieceStart) * lastFiniteIdx) / Math.max(1, n - 1);
        const yVal = piece.sample[lastFiniteIdx] as number;
        const sx = axes.toScreenX(xVal);
        const sy = axes.toScreenY(yVal);
        const labelClass = piece.className.replace('fn-curve-', 'fn-curve-label-');
        const labelGroup = createSvgEl('g', { class: `fn-curve-label-group ${labelClass}` });
        const anchorEnd = sx > state.width - 80;
        const tx = anchorEnd
          ? Math.min(sx - 6, state.width - 4)
          : Math.min(sx + 8, state.width - 4);
        const ty = Math.max(18, sy - 8);
        const pad = 4;
        const approxWidth = piece.label.length * 7 + pad * 2;
        const rectX = anchorEnd ? tx - approxWidth + pad : tx - pad;
        const rect = createSvgEl('rect', {
          x: rectX,
          y: ty - 12,
          width: approxWidth,
          height: 16,
          rx: 3,
          class: 'fn-curve-label-bg',
        });
        labelGroup.appendChild(rect);
        const text = createSvgEl('text', {
          x: tx,
          y: ty,
          class: `fn-curve-label ${labelClass}`,
          'text-anchor': anchorEnd ? 'end' : 'start',
        });
        text.textContent = piece.label;
        labelGroup.appendChild(text);
        root.appendChild(labelGroup);
      }
    }

    for (const m of state.endpointMarkers) {
      const sx = axes.toScreenX(m.x);
      const sy = axes.toScreenY(m.y);
      if (m.kind === 'closed') {
        root.appendChild(
          createSvgEl('circle', {
            cx: sx,
            cy: sy,
            r: 5,
            class: 'fn-endpoint-closed',
          }),
        );
      } else {
        root.appendChild(
          createSvgEl('circle', {
            cx: sx,
            cy: sy,
            r: 5,
            class: 'fn-endpoint-open',
            fill: 'transparent',
          }),
        );
      }
    }

    for (const t of state.discontinuityTicks) {
      const sx = axes.toScreenX(t.x);
      const sy = axes.toScreenY(state.yDomain[0]);
      root.appendChild(
        createSvgEl('line', {
          x1: sx,
          y1: sy - 6,
          x2: sx,
          y2: sy + 6,
          class: `fn-discontinuity-tick fn-discontinuity-${t.kind}`,
        }),
      );
    }
  }

  render();

  return {
    group: parent,
    update(partial) {
      state = { ...state, ...partial };
      render();
    },
    destroy() {
      root.remove();
    },
  };
}
